import { ContentAlignmentHorizontal } from './ContentAlignmentHorizontal';
import { ContentAlignmentVertical } from '../ContentAlignmentVertical/ContentAlignmentVertical';
import { useNode } from '@craftjs/core';
import type { ContainerProps } from '@builder4/shared/components/Container';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { getBreakpointProp } from '@builder4/shared/utils/breakpointUtils';
import { useAppSelector } from '@/store/hooks';
import { selectBreakpoint } from '@/store/system/selectors';

const horizontalMapping = {
  left: 'flex-start',
  right: 'flex-end',
  center: 'center',
  'flex-start': 'left',
  'flex-end': 'right',
};

const verticalMapping = {
  top: 'flex-start',
  bottom: 'flex-end',
  center: 'center',
  'flex-start': 'top',
  'flex-end': 'bottom',
};
type Alignment = 'flex-start' | 'flex-end' | 'center';

const NodeContentAlignmentSettings = () => {
  const {
    nodeProps,
    displayName,
    actions: { setProp },
  } = useNode((node) => {
    return {
      nodeProps: node.data.props as ContainerProps,
      displayName: node.data.displayName,
    };
  });

  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);
  const breakpoint = useAppSelector(selectBreakpoint);

  const alignItems = getBPProp(nodeProps, 'alignItems') ?? 'center';
  const flexDirection = getBPProp(nodeProps, 'flexDirection') ?? 'column';
  const justifyContent = getBPProp(nodeProps, 'justifyContent') ?? 'center';

  const halignedContent = horizontalMapping[flexDirection === 'row' ? justifyContent : alignItems];
  const valignedContent = verticalMapping[flexDirection === 'row' ? alignItems : justifyContent];

  const getCurrentFlexDirection = (props: Partial<ContainerProps>) => {
    const currentFlexDirection = getBreakpointProp(props, 'flexDirection', breakpoint);
    return currentFlexDirection ?? flexDirection;
  };

  const handleHAlignmentChange = (alignment: string) => {
    const alignedContent = horizontalMapping[alignment as keyof typeof horizontalMapping];

    setProp((props: Partial<ContainerProps>) => {
      const flexDir = getCurrentFlexDirection(props);

      if (flexDir === 'row') {
        setBPProp(props, 'justifyContent', alignedContent as Alignment);
      } else {
        setBPProp(props, 'alignItems', alignedContent as Alignment);
      }
    });
  };
  const handleVAlignmentChange = (alignment: string) => {
    const alignedContent = verticalMapping[alignment as keyof typeof verticalMapping];

    setProp((props: Partial<ContainerProps>) => {
      const flexDir = getCurrentFlexDirection(props);

      if (flexDir === 'row') {
        setBPProp(props, 'alignItems', alignedContent as Alignment);
      } else {
        setBPProp(props, 'justifyContent', alignedContent as Alignment);
      }
    });
  };

  return (
    <div
      className="flex flex-col gap-3"
      data-testid="content-alignment-settings"
      data-breakpoint={breakpoint}
    >
      <ContentAlignmentHorizontal
        onClick={handleHAlignmentChange}
        align={halignedContent}
        text="H Alignment"
      />

      <ContentAlignmentVertical
        onClick={handleVAlignmentChange}
        align={valignedContent}
        text="V Alignment"
      />
    </div>
  );
};

export default NodeContentAlignmentSettings;
